import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ClipboardList, MessageCircle, Radar, Send, Users } from 'lucide-react';
import { WHATSAPP_LINK } from '../constants';

const SALES_AGENT_STEPS = [
  {
    title: 'Captura automática',
    description: 'Entradas desde WhatsApp, Instagram, formularios o email centralizadas como oportunidades.',
    icon: Radar,
  },
  {
    title: 'Cualificación',
    description: 'Servicio de interés, urgencia, presupuesto, disponibilidad, ubicación y objeciones principales.',
    icon: Users,
  },
  {
    title: 'Seguimiento',
    description: 'Recordatorios internos, mensajes sugeridos y recuperación de interesados que se enfrían.',
    icon: Send,
  },
  {
    title: 'Pipeline visible',
    description: 'Nuevo lead, interesado, pendiente de respuesta, propuesta enviada, ganado o perdido.',
    icon: ClipboardList,
  },
];

const SALES_AGENT_WHATSAPP = `${WHATSAPP_LINK}?text=${encodeURIComponent('Hola, me interesa el módulo de agentes de ventas IA para mi negocio.')}`;

export const SalesAgentsSection: React.FC = () => {
  return (
    <section id="agentes-ventas" className="py-24 bg-slate-950 text-white relative overflow-hidden dark-noise">
      <img
        src="/img/sales-agents-background.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-45"
        style={{ objectPosition: '62% 50%' }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/88 to-slate-950/48" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/55 via-transparent to-slate-950/85" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(60,179,113,0.18),transparent_35%),radial-gradient(circle_at_80%_60%,rgba(34,211,238,0.12),transparent_35%)]" />
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span
              className="inline-flex items-center gap-2 py-1.5 px-5 rounded-full text-xs font-bold mb-5 uppercase tracking-[0.2em]"
              style={{
                background: 'rgba(60,179,113,0.12)',
                border: '1px solid rgba(60,179,113,0.35)',
                color: '#7ee8a2',
              }}
            >
              Ventas IA + seguimiento
            </span>
            <h2 className="font-black mb-5 font-display" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}>
              Agentes de ventas que no dejan oportunidades olvidadas
            </h2>
            <p className="text-slate-300 text-lg md:text-xl font-light leading-relaxed mb-8">
              Ancora no solo responde mensajes: detecta interesados, cualifica leads, propone el siguiente paso y mantiene el seguimiento hasta que el cliente decide.
            </p>
            <div className="space-y-3 mb-8">
              {[
                'Captura leads de WhatsApp, Instagram, formularios y email.',
                'Resume el contexto y recomienda la próxima acción.',
                'Mantiene un pipeline claro para owner/equipo comercial.',
                'Funciona como módulo separado: sin spam ni envíos sin aprobación.',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 text-slate-300">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-emerald-400 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <a
              href={SALES_AGENT_WHATSAPP}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-bold text-slate-950 transition hover:-translate-y-1"
              style={{ background: 'linear-gradient(135deg, #3CB371, #7ee8a2)', boxShadow: '0 0 30px rgba(60,179,113,0.28)' }}
            >
              <MessageCircle size={18} />
              Quiero ver cómo funcionaría
              <ArrowRight size={16} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 gap-5"
          >
            {SALES_AGENT_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="rounded-3xl p-6"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)' }}
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'rgba(60,179,113,0.16)' }}>
                    <Icon size={24} className="text-emerald-300" />
                  </div>
                  <h3 className="text-lg font-black mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed font-light">{step.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
